import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAdmin } from '../contexts/AdminContext';
import {
  IoGridOutline,
  IoGridSharp,
  IoPeopleOutline,
  IoPeopleSharp,
  IoShieldOutline,
  IoShieldSharp,
  IoHomeOutline,
  IoHomeSharp,
  IoSettingsOutline,
  IoLogOutOutline,
  IoChevronBackOutline
} from 'react-icons/io5';
import { useAuth } from '../hooks/useAuth';
import Avatar from './Avatar';
import './AdminLayout.css';

const AdminLayout = ({ children }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { permissions, hasPermission, loading } = useAdmin();
  const { user } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Define navigation items with required permissions
  const navItems = [
    {
      id: 'dashboard',
      label: t('admin.nav.dashboard', 'Dashboard'),
      path: '/admin',
      icon: IoGridOutline,
      activeIcon: IoGridSharp,
      permission: 'dashboard'
    },
    {
      id: 'users',
      label: t('admin.nav.users', 'Users'),
      path: '/admin/users',
      icon: IoPeopleOutline,
      activeIcon: IoPeopleSharp,
      permission: 'manage_users'
    },
    {
      id: 'groups',
      label: t('admin.nav.groups', 'Groups'),
      path: '/admin/groups',
      icon: IoShieldOutline,
      activeIcon: IoShieldSharp,
      permission: 'manage_groups'
    }
  ];

  // Filter nav items based on permissions
  const visibleNavItems = navItems.filter(item => {
    // Admins see everything
    if (user?.is_admin) return true;
    // Non-admins need specific permission
    return hasPermission(item.permission);
  });

  const isActive = (path) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  if (loading) {
    return (
      <div className="admin-layout admin-loading">
        <div className="admin-loading-spinner">Loading...</div>
      </div>
    );
  }

  return (
    <div className={`admin-layout ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div className="admin-sidebar-header">
          <Link to="/" className="admin-logo">
            <IoHomeOutline className="admin-logo-icon" />
            {!sidebarCollapsed && <span className="admin-logo-text">Emoozi</span>}
          </Link>
          <button
            className="sidebar-toggle"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <IoChevronBackOutline className={sidebarCollapsed ? 'rotated' : ''} />
          </button>
        </div>

        <nav className="admin-nav">
          <div className="admin-nav-section">
            {!sidebarCollapsed && <span className="admin-nav-section-title">Menu</span>}
            {visibleNavItems.map(item => {
              const Icon = isActive(item.path) ? item.activeIcon : item.icon;
              return (
                <Link
                  key={item.id}
                  to={item.path}
                  className={`admin-nav-item ${isActive(item.path) ? 'active' : ''}`}
                  title={sidebarCollapsed ? item.label : undefined}
                >
                  <Icon className="admin-nav-icon" />
                  {!sidebarCollapsed && <span className="admin-nav-label">{item.label}</span>}
                </Link>
              );
            })}
          </div>
        </nav>

        <div className="admin-sidebar-footer">
          <Link to="/" className="admin-nav-item" title={sidebarCollapsed ? 'Back to site' : undefined}>
            <IoHomeOutline className="admin-nav-icon" />
            {!sidebarCollapsed && <span className="admin-nav-label">Back to Site</span>}
          </Link>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        {/* Header */}
        <header className="admin-header">
          <div className="admin-header-left">
            <h1 className="admin-page-title">
              {navItems.find(item => isActive(item.path))?.label || 'Dashboard'}
            </h1>
          </div>
          <div className="admin-header-right">
            <div className="admin-user">
              <Avatar user={user} size="small" />
              <span className="admin-user-name">{user?.display_name || user?.username}</span>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="admin-content">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
